import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/* eslint-disable import/order */
import '@/styles/Board/Toolbars.less';
import { useAtom } from 'jotai';
import { useEffect, useRef } from 'react';
import { useDrop } from 'react-dnd';
import { PresentationButton } from '@/components/Board/PresentationPanel/PresentationButton';
import { PresentationFrameItem } from '@/components/Board/PresentationPanel/PresentationFrameItem';
import { selectedIdsAtom } from '@/store/boardAtoms';
const PRESENTATION_FRAME_TYPE = 'PRESENTATION_FRAME';
export default function PresentationPanel({ presentationElements, onReorderPresentation, onSetActiveFrame, onUpdateElementName, onPrevFrame, onNextFrame, styles, presentationMode, onStartPresentation, onStopPresentation, onCenterOnElement, inline = false, }) {
    const [selectedIds, setSelectedIds] = useAtom(selectedIdsAtom);
    const scrollContainerRef = useRef(null);
    const autoScrollIntervalRef = useRef(null);
    const stopAutoScroll = () => {
        if (autoScrollIntervalRef.current !== null) {
            clearInterval(autoScrollIntervalRef.current);
            autoScrollIntervalRef.current = null;
        }
    };
    const [{ isOver }, drop] = useDrop({
        accept: PRESENTATION_FRAME_TYPE,
        collect: (monitor) => ({
            isOver: monitor.isOver(),
        }),
        hover: (item, monitor) => {
            if (!scrollContainerRef.current || !monitor.isOver({ shallow: true })) {
                stopAutoScroll();
                return;
            }
            const container = scrollContainerRef.current;
            const clientOffset = monitor.getClientOffset();
            if (!clientOffset)
                return;
            const rect = container.getBoundingClientRect();
            const mouseY = clientOffset.y - rect.top;
            const SCROLL_ZONE = 50;
            const SCROLL_SPEED = 10;
            stopAutoScroll();
            const scrollUp = mouseY < SCROLL_ZONE && container.scrollTop > 0;
            const scrollDown = mouseY > rect.height - SCROLL_ZONE &&
                container.scrollTop < container.scrollHeight - container.clientHeight;
            if (scrollUp || scrollDown) {
                autoScrollIntervalRef.current = window.setInterval(() => {
                    if (!scrollContainerRef.current)
                        return;
                    const current = scrollContainerRef.current;
                    if (scrollUp) {
                        current.scrollTop = Math.max(0, current.scrollTop - SCROLL_SPEED);
                    }
                    else if (scrollDown) {
                        current.scrollTop = Math.min(current.scrollHeight - current.clientHeight, current.scrollTop + SCROLL_SPEED);
                    }
                }, 16);
            }
        },
    });
    useEffect(() => {
        return stopAutoScroll;
    }, []);
    useEffect(() => {
        if (!isOver)
            stopAutoScroll();
    }, [isOver]);
    const reorderArray = (elements, from, to) => {
        const ids = elements.map((item) => item.id);
        ids.splice(to, 0, ids.splice(from, 1)[0]);
        return ids.map((id, index) => {
            const el = elements.find((el) => el.id === id);
            if (!el)
                throw new Error(`Element with id ${id} not found`);
            return Object.assign(Object.assign({}, el), { presentationOrder: index });
        });
    };
    const reorderElements = (from, to) => {
        if (!onReorderPresentation)
            return;
        onReorderPresentation(reorderArray(presentationElements, from, to));
    };
    const getElementIndex = (element) => presentationElements.findIndex((el) => el.id === element.id);
    const moveElementUp = (element) => {
        const index = getElementIndex(element);
        if (index > 0)
            reorderElements(index, index - 1);
    };
    const moveElementDown = (element) => {
        const index = getElementIndex(element);
        if (index < presentationElements.length - 1)
            reorderElements(index, index + 1);
    };
    const handleClickElement = (element) => {
        setSelectedIds([element.id]);
        onCenterOnElement === null || onCenterOnElement === void 0 ? void 0 : onCenterOnElement(element);
    };
    const isElementSelected = (element) => selectedIds.includes(element.id);
    const activeIndex = presentationElements.findIndex((el) => el.isActivePresentationFrame);
    const total = presentationElements.length;
    return (_jsxs("div", { style: Object.assign({ position: inline ? 'relative' : 'fixed', right: inline ? undefined : 16, top: inline ? undefined : 16, maxWidth: 560, minWidth: 360, width: 'fit-content', minHeight: '50%', height: 'calc(100vh - 100px)', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', gap: 8, borderRadius: 10, background: 'rgba(24,24,24,0.89)', color: '#fff', padding: 16, boxShadow: '0 2px 6px rgba(0,0,0,0.25)', pointerEvents: 'auto' }, styles), children: [_jsx("div", { style: { marginBottom: 4, fontSize: 15, fontWeight: 600, color: '#A6B4C0' }, children: "Presentation frames" }), _jsx("div", { ref: (node) => {
                    scrollContainerRef.current = node;
                    drop(node);
                }, className: "presentation-frames", style: {
                    display: 'flex',
                    flex: 1,
                    flexDirection: 'column',
                    gap: 4,
                    overflowY: 'auto',
                }, children: presentationElements.map((element, index) => (_jsx(PresentationFrameItem, { element: element, index: index, total: presentationElements.length, isSelected: isElementSelected(element), isPresentationMode: presentationMode, onSelect: handleClickElement, onSetActiveFrame: onSetActiveFrame || (() => { }), onMoveUp: moveElementUp, onMoveDown: moveElementDown, onReorder: reorderElements, onUpdateName: onUpdateElementName || (() => { }), dragType: PRESENTATION_FRAME_TYPE }, element.id))) }), _jsxs("div", { className: "presentation-controls", style: { display: 'flex', flexDirection: 'column' }, children: [_jsxs("div", { style: {
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 8,
                            marginBottom: 8,
                        }, children: [_jsx("button", { onClick: onPrevFrame, disabled: activeIndex <= 0, style: {
                                    borderRadius: 6,
                                    padding: '6px 10px',
                                    color: '#000',
                                    backgroundColor: activeIndex <= 0 ? '#494949ff' : '#919191ff',
                                    cursor: activeIndex <= 0 ? 'not-allowed' : 'pointer',
                                }, title: "\u041F\u0440\u0435\u0434\u044B\u0434\u0443\u0449\u0438\u0439 \u0444\u0440\u0435\u0439\u043C", children: "\u2190 \u041D\u0430\u0437\u0430\u0434" }), _jsx("div", { style: { minWidth: 100, textAlign: 'center', color: 'rgba(255,255,255,0.9)' }, children: total > 0 ? `${Math.max(1, activeIndex + 1)} / ${total}` : '0 / 0' }), _jsx("button", { onClick: onNextFrame, disabled: activeIndex === -1 || activeIndex >= total - 1, style: {
                                    borderRadius: 6,
                                    padding: '6px 10px',
                                    color: '#000',
                                    backgroundColor: activeIndex === -1 || activeIndex >= total - 1 ? '#494949ff' : '#919191ff',
                                    cursor: activeIndex === -1 || activeIndex >= total - 1 ? 'not-allowed' : 'pointer',
                                }, title: "\u0421\u043B\u0435\u0434\u0443\u044E\u0449\u0438\u0439 \u0444\u0440\u0435\u0439\u043C", children: "\u0412\u043F\u0435\u0440\u0451\u0434 \u2192" })] }), _jsx(PresentationButton, { presentationElements: presentationElements, presentationMode: presentationMode, onStartPresentation: onStartPresentation, onStopPresentation: onStopPresentation })] })] }));
}
